import { takeLatest, put, call, all, fork } from 'redux-saga/effects';
import axios from '../../util/Api';
import { toast } from 'react-toastify';
import {
  updateFormState,
  setCurrentStep,
  fetchDirectAccessDataSuccess,
  fetchDirectAccessLoading,
  fetchDirectAccessError,
  setQueryParams,
} from '../actions/WizardLaunchActions';
import { decryptData } from '../../util/crypto';
import * as types from '../../constants/ActionTypes';

// Dynamic data API call
const fetchDynamicDataAPI = async (sentFormData) => {
  try {
    const formData = new FormData();
    // Append the entire sentFormData object as a single key-value pair
    formData.append('sentFormData', JSON.stringify(sentFormData));
    const response = await axios.post('/ws_get_survey_question', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  } catch (error) {
    console.error('API Error:', error);
    throw (
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch dynamic data'
    );
  }
};

function* fetchDynamicDataSaga(action) {
  try {
    const response = yield call(fetchDynamicDataAPI, action.payload);

    // Update Redux with the response
    yield put({
      type: types.FETCH_DYNAMIC_DATA_SUCCESS,
      payload: response,
    });

    // Handle different question types
    switch (response.question_type) {
      case 'select':
        yield put(
          updateFormState({
            navigationPath: '/state-selection',
            currentStep: '/state-selection',
          })
        );
        yield put(setCurrentStep('/state-selection'));
        break;

      case 'cdl_type':
        yield put(
          updateFormState({
            navigationPath: '/cdl-type',
            currentStep: '/cdl-type',
          })
        );
        yield put(setCurrentStep('/cdl-type'));
        break;

      case 'endorsements':
        yield put(
          updateFormState({
            navigationPath: '/endorsement-selection',
            currentStep: '/endorsement-selection',
          })
        );
        yield put(setCurrentStep('/endorsement-selection'));
        break;

      case 'video':
        yield put(
          updateFormState({
            navigationPath: '/video-selection',
            currentStep: '/video-selection',
          })
        );
        yield put(setCurrentStep('/video-selection'));
        break;

      case 'recommend':
      case 'course_recommendation':
        yield put(
          updateFormState({
            navigationPath: '/course-recommendation',
            currentStep: '/course-recommendation',
          })
        );
        yield put(setCurrentStep('/course-recommendation'));
        break;

      case 'multi_select_courses':
      case 'more_class_selection':
        yield put(
          updateFormState({
            navigationPath: '/more-class-selection',
            currentStep: '/more-class-selection',
          })
        );
        yield put(setCurrentStep('/more-class-selection'));
        break;

      default:
    }
  } catch (error) {
    console.error('Saga: API call failed:', error);
    const errorMessage = error.message || 'An error occurred';
    toast.error(errorMessage);
    yield put({
      type: types.FETCH_DYNAMIC_DATA_FAILURE,
      payload: errorMessage,
    });
  }
}

// Direct access course API call with AbortController for cancellation
const fetchDirectAccessAPI = async (params, signal) => {
  try {
    const formData = new FormData();
    formData.append('sentFormData', JSON.stringify(params));
    
    const response = await axios.post('/ws_get_courses_by_instructor_company', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      signal, // Pass the AbortSignal to axios for cancellation support
    });
    
    return response.data;
  } catch (error) {
    // Check if the request was cancelled (aborted)
    if (axios.isCancel(error)) {
      console.log('Request cancelled:', error.message);
      // Return a special value or throw a specific error to handle cancellation
      throw new Error('REQUEST_CANCELLED');
    }
    
    console.error('API Error:', error);
    throw (
      error.response?.data?.error ||
      error.message ||
      'Failed to fetch direct access data'
    );
  }
};

// Saga to handle direct access URL parameter with proper cancellation handling
function* fetchDirectAccessSaga(action) {
  // Create an AbortController for cancellation support
  const abortController = new AbortController();
  const { signal } = abortController;
  
  try {
    // Extract the access token from the URL query parameter
    const { access } = action.payload;
    
    if (!access) {
      throw new Error('No access token provided');
    }
    
    // Decrypt the access token
    const decrypted = yield call(decryptData, access);
    
    if (!decrypted) {
      throw new Error('Access token is not valid');
    }
    
    // Parse the decrypted string as URL parameters
    const params = new URLSearchParams(decrypted);
    const paramObj = {};
    
    for (const [key, value] of params.entries()) {
      paramObj[key] = value;
    }
    
    // Store the decrypted parameters in Redux
    yield put(setQueryParams(paramObj));
    
    // Clear any existing endorsements and more class selections to prevent them from appearing in the cart
    // alongside direct access courses
    yield put({ type: types.CLEAR_ENDORSEMENTS });
    yield put({ type: types.CLEAR_MORE_CLASS_SELECTION });
    
    // Set loading state
    yield put(fetchDirectAccessLoading(true));
    
    try {
      // Make API call to fetch direct access data with the abort signal
      const response = yield call(fetchDirectAccessAPI, paramObj, signal);
      // Process the response data
      // const dataWithOptions = response.options.map(course => ({
      //   ...course,
      // }));
      
      // Update Redux with the processed data
      yield put(fetchDirectAccessDataSuccess({
        options: response.options || [],
        question_text: response.question_text || "What Course are you seeking for your CDL?",
        question_type: "directcourseaccess",
        offer: response.offer || "$0"
      }));
    } catch (error) {
      // Handle cancellation specifically
      if (error.message === 'REQUEST_CANCELLED') {
        console.log('Direct access request was cancelled');
        return; // Exit the saga without showing error messages
      }
      throw error; // Re-throw for the outer catch block to handle
    }
  } catch (error) {
    console.error('Direct Access Saga Error:', error);
    // Don't show error toast if the component was unmounted (request cancelled)
    if (error.message !== 'REQUEST_CANCELLED') {
      const errorMessage = error.message || 'An error occurred while processing direct access';
      toast.error(errorMessage);
      yield put(fetchDirectAccessError(errorMessage));
    }
  } finally {
    // Always clean up loading state unless the request was cancelled
    yield put(fetchDirectAccessLoading(false));
  }
}

// Add a cleanup action type for cancellation
const CANCEL_DIRECT_ACCESS_REQUEST = 'CANCEL_DIRECT_ACCESS_REQUEST';

// Modified watcher saga with proper cancellation handling
export function* watchWizardLaunch() {
  yield takeLatest(types.FETCH_DYNAMIC_DATA_REQUEST, fetchDynamicDataSaga);
  
  // For direct access, use takeLatest which automatically cancels previous requests
  // when a new one comes in, helping prevent the async message channel error
  yield takeLatest('FETCH_DIRECT_ACCESS_REQUEST', fetchDirectAccessSaga);
  
  // Add a specific cancellation handler for direct access requests
  yield takeLatest(CANCEL_DIRECT_ACCESS_REQUEST, function* () {
    // This saga doesn't need to do anything - takeLatest will cancel the previous saga
    console.log('Direct access request cancelled explicitly');
  });
}

export default function* rootSaga() {
  yield all([fork(watchWizardLaunch)]);
}
